import { Controller } from "@hotwired/stimulus";
import { CHIP_CLASS, isChip, serializeSurface, syncEndMarker } from "./placeholder-editor-surface";
import { deriveLabel, parsePlaceholders } from "./placeholder-tokens";

interface PlaceholderInfo {
   name: string;
   label: string;
   isGlobal: boolean;
}

function isLineBreakInsert(inputType: string): boolean {
   return inputType === "insertParagraph" || inputType === "insertLineBreak";
}

/** The transfer's plain text with normalised line endings; formatting is never read. */
function plainText(transfer: DataTransfer | null): string {
   return (transfer?.getData("text/plain") ?? "").replace(/\r\n?/g, "\n");
}

/** The caret position under the pointer, or null when the browser cannot resolve one. */
function caretRangeAtPoint(x: number, y: number): Range | null {
   if (typeof document.caretPositionFromPoint === "function") {
      const position = document.caretPositionFromPoint(x, y);
      if (!position) {
         return null;
      }

      const range = document.createRange();
      range.setStart(position.offsetNode, position.offset);
      return range;
   }

   if (typeof document.caretRangeFromPoint === "function") {
      return document.caretRangeFromPoint(x, y);
   }

   return null;
}

function selectionRange(): Range | null {
   const selection = window.getSelection();
   return selection && selection.rangeCount > 0 ? selection.getRangeAt(0) : null;
}

// ---------------------------------------------------------------------------
// Controller
// ---------------------------------------------------------------------------

/**
 * Turns a hidden input holding a raw Translation Value into a chip editor. Translators never see raw
 * `{token}` braces: each available token renders as a friendly chip, insertion is menu-driven, and a
 * token not in the available set renders as inert literal text. The default-value column locks its
 * placeholder set (no insert menu, chip deletion blocked).
 */
export default class extends Controller {
   static targets = ["input", "surface", "menu"];
   static values = {
      isDefaultValue: { type: Boolean, default: false },
      available: { type: Array, default: [] },
      globalLabel: { type: String, default: "" }
   };

   declare readonly inputTarget: HTMLInputElement;
   declare readonly surfaceTarget: HTMLElement;
   declare readonly hasMenuTarget: boolean;
   declare readonly menuTarget: HTMLElement;
   declare readonly isDefaultValueValue: boolean;
   declare readonly availableValue: PlaceholderInfo[];
   declare readonly globalLabelValue: string;

   #availableByName: Map<string, PlaceholderInfo> = new Map();
   /** Aborted on disconnect, which removes every listener `connect` adds. */
   #listeners?: AbortController;
   /** The selection being dragged out of this surface, from `dragstart` until `dragend`. */
   #dragSource: Range | null = null;

   connect() {
      this.#availableByName = new Map(this.availableValue.map((p) => [p.name, p]));

      this.#renderSurface(this.inputTarget.value);

      if (this.isDefaultValueValue) {
         // Default-value lock: no inserting, no removing chips.
         if (this.hasMenuTarget) {
            this.menuTarget.hidden = true;
         }
      } else {
         this.#renderMenu();
      }

      this.#listeners = new AbortController();
      const signal = this.#listeners.signal;
      const surface = this.surfaceTarget;

      // Keep the hidden input in sync as the surface is edited.
      surface.addEventListener("input", () => this.#commitEdit(), { signal });

      // Enter inserts one newline; the locked default-value editor also refuses edits that would remove a chip.
      surface.addEventListener("beforeinput", (event) => this.#onBeforeInput(event), { signal });
      surface.addEventListener("keydown", (event) => this.#onKeyDown(event), { signal });

      // Paste and drop insert plain text only; the browser would keep the source's formatting.
      surface.addEventListener("paste", (event) => this.#onPaste(event), { signal });

      // A drag that starts in the surface moves its selection when it drops back in the surface.
      surface.addEventListener("dragstart", () => this.#onDragStart(), { signal });
      surface.addEventListener("dragend", () => { this.#dragSource = null; }, { signal });
      surface.addEventListener("drop", (event) => this.#onDrop(event), { signal });

      // Serialize one final time right before the form submits, in case the last edit raced the input event.
      this.element.closest("form")?.addEventListener("submit", () => this.serialize(), { signal });

      // Make sure the hidden input holds the canonical serialization from the start.
      this.serialize();
   }

   disconnect() {
      this.#listeners?.abort();
   }

   // ----------------
   // Stimulus Actions
   // ----------------

   /**
    * Inserts the placeholder named by the clicked button's `data-name` at the current caret position
    * (fallback: append to the end of the surface).
    */
   insert(event: Event) {
      if (this.isDefaultValueValue) {
         return; // Locked: the menu is hidden, but guard anyway.
      }

      const button = event.currentTarget as HTMLElement;
      const name = button.getAttribute("data-name");

      if (!name) {
         return;
      }

      this.#insertNodeAt(this.#createChip(name), this.#surfaceRange());
      this.#commitEdit();
   }

   /**
    * Loads a historical raw value into this editor: re-renders the surface (so it shows chips, not raw
    * braces) and updates the hidden input. The value is read straight off the clicked element's
    * `data-history-value` attribute to keep the exact string (no Stimulus param type-casting).
    */
   restore(event: Event) {
      const button = event.currentTarget as HTMLElement;
      const rawValue = button.getAttribute("data-history-value") ?? "";

      this.#renderSurface(rawValue);
      this.serialize();
   }

   serialize() {
      this.inputTarget.value = serializeSurface(this.surfaceTarget);
   }

   // ---------------
   // Private methods
   // ---------------

   #renderSurface(rawValue: string) {
      this.surfaceTarget.replaceChildren(this.#renderValue(rawValue));
      syncEndMarker(this.surfaceTarget);
   }

   #renderValue(rawValue: string): DocumentFragment {
      const fragment = document.createDocumentFragment();

      for (const segment of parsePlaceholders(rawValue)) {
         if (segment.kind === "literal") {
            fragment.appendChild(document.createTextNode(segment.text));
            continue;
         }

         // Token: render as a chip only if it is in the available set; otherwise inert literal `{name}`.
         if (this.#availableByName.has(segment.name)) {
            fragment.appendChild(this.#createChip(segment.name));
         } else {
            fragment.appendChild(document.createTextNode("{" + segment.name + "}"));
         }
      }

      return fragment;
   }

   #commitEdit() {
      syncEndMarker(this.surfaceTarget);
      this.serialize();
   }

   #createChip(name: string): HTMLSpanElement {
      const info = this.#availableByName.get(name);
      const label = info && info.label ? info.label : deriveLabel(name);

      const chip = document.createElement("span");
      chip.className = CHIP_CLASS;
      chip.setAttribute("contenteditable", "false");
      chip.setAttribute("data-name", name);
      chip.textContent = label;
      return chip;
   }

   #renderMenu() {
      if (!this.hasMenuTarget) {
         return;
      }

      this.menuTarget.replaceChildren();

      for (const placeholder of this.availableValue) {
         const button = document.createElement("button");
         button.type = "button";
         button.className = "placeholder-insert-button";
         button.setAttribute("data-name", placeholder.name);
         button.setAttribute("data-action", "placeholder-editor#insert");
         button.textContent = placeholder.label;

         if (placeholder.isGlobal) {
            // Subtle marker that this placeholder is a project-wide global.
            const marker = document.createElement("span");
            marker.className = "placeholder-global-marker";
            marker.textContent = this.globalLabelValue;
            button.appendChild(document.createTextNode(" "));
            button.appendChild(marker);
         }

         this.menuTarget.appendChild(button);
      }
   }

   #replaceSelectionWithText(text: string) {
      if (this.isDefaultValueValue && this.#replaceWouldRemoveChip()) {
         return;
      }

      if (text !== "") {
         this.#insertNodeAt(document.createTextNode(text), this.#surfaceRange());
         this.#commitEdit();
      }
   }

   #insertNodeAt(node: Node, range: Range | null) {
      // A fragment is emptied by the insert, so the caret goes after its last child.
      const last = node.nodeType === Node.DOCUMENT_FRAGMENT_NODE ? node.lastChild! : node;

      if (!range) {
         this.surfaceTarget.appendChild(node);
         return;
      }

      range.deleteContents();
      range.insertNode(node);
      range.setStartAfter(last);
      range.collapse(true);

      const selection = window.getSelection()!;
      selection.removeAllRanges();
      selection.addRange(range);
   }

   #surfaceRange(): Range | null {
      const range = selectionRange();
      return range && this.surfaceTarget.contains(range.commonAncestorContainer) ? range : null;
   }

   #onBeforeInput(event: InputEvent) {
      if (isLineBreakInsert(event.inputType)) {
         // The browser would add its own <div>/<br> markup; insert one newline instead.
         event.preventDefault();
         this.#replaceSelectionWithText("\n");
         return;
      }

      if (!this.isDefaultValueValue) {
         return;
      }

      // In the locked default editor, block any edit that would delete a chip. Deletions that only
      // touch surrounding text are allowed.
      if (event.inputType.startsWith("delete") && this.#deleteWouldRemoveChip()) {
         event.preventDefault();
      }
   }

   #onPaste(event: ClipboardEvent) {
      event.preventDefault();
      this.#replaceSelectionWithText(plainText(event.clipboardData));
   }

   #onDragStart() {
      const range = this.#surfaceRange();
      this.#dragSource = range && !range.collapsed ? range.cloneRange() : null;
   }

   /** A selection dragged within the surface moves; any other drop inserts its plain text. */
   #onDrop(event: DragEvent) {
      event.preventDefault();

      const at = this.#dropRange(event);

      if (this.#dragSource) {
         this.#moveTo(this.#dragSource, at);
         return;
      }

      const text = plainText(event.dataTransfer);
      if (text !== "") {
         this.#insertNodeAt(document.createTextNode(text), at);
         this.#commitEdit();
      }
   }

   /**
    * Moves the dragged selection to the drop point. It is rebuilt from its raw value, so chips stay
    * chips and no markup comes along. No chip is added or removed, so the locked default editor
    * allows it too.
    */
   #moveTo(source: Range, at: Range | null) {
      const position = at ? source.comparePoint(at.startContainer, at.startOffset) : 1;
      if (position === 0) {
         return; // Dropped onto the selection itself.
      }

      const dragged = document.createElement("div");
      dragged.appendChild(source.cloneContents());
      const rawValue = serializeSurface(dragged);
      if (rawValue === "") {
         return;
      }

      // The edit further along goes first, so it cannot shift the other position.
      const moved = this.#renderValue(rawValue);
      if (position < 0) {
         source.deleteContents();
         this.#insertNodeAt(moved, at);
      } else {
         this.#insertNodeAt(moved, at);
         source.deleteContents();
      }

      this.#commitEdit();
   }

   /** Where a drop inserts; null appends. */
   #dropRange(event: DragEvent): Range | null {
      const point = caretRangeAtPoint(event.clientX, event.clientY);

      if (point && this.surfaceTarget.contains(point.startContainer)) {
         // A chip is not editable: a point in its label or on the chip itself drops beside it.
         const container = point.startContainer;
         const element = container instanceof Element ? container : container.parentElement;
         const chip = element?.closest(`.${CHIP_CLASS}`);
         if (chip && this.surfaceTarget.contains(chip)) {
            point.setStartAfter(chip);
         }

         point.collapse(true);
         return point;
      }

      const caret = this.#surfaceRange();
      caret?.collapse(false);
      return caret;
   }

   #onKeyDown(event: KeyboardEvent) {
      if (!this.isDefaultValueValue) {
         return;
      }

      if ((event.key === "Backspace" || event.key === "Delete") && this.#deleteWouldRemoveChip()) {
         event.preventDefault();
      }
   }

   /**
    * A delete removes the chips a selection covers, or the chip beside a collapsed caret. Only a
    * selection inside the surface counts.
    */
   #deleteWouldRemoveChip(): boolean {
      const range = this.#surfaceRange();

      if (!range) {
         return false;
      }

      return range.collapsed ? this.#chipAdjacentToCaret(range) : this.#rangeIntersectsChip(range);
   }

   /** Replacing a selection removes the chips it covers, also when it reaches past the surface. */
   #replaceWouldRemoveChip(): boolean {
      const range = selectionRange();
      return range !== null && !range.collapsed && this.#rangeIntersectsChip(range);
   }

   #rangeIntersectsChip(range: Range): boolean {
      return Array.from(this.surfaceTarget.querySelectorAll(`.${CHIP_CLASS}`))
         .some((chip) => range.intersectsNode(chip));
   }

   #chipAdjacentToCaret(range: Range): boolean {
      const container = range.startContainer;
      const offset = range.startOffset;

      if (container.nodeType === Node.ELEMENT_NODE) {
         const children = container.childNodes;
         // Caret sits between children[offset-1] and children[offset].
         if (isChip(children[offset - 1] ?? null) || isChip(children[offset] ?? null)) {
            return true;
         }
      } else if (container.nodeType === Node.TEXT_NODE) {
         // Caret at the very start of a text node: a preceding chip would be removed by Backspace.
         if (offset === 0 && isChip(container.previousSibling)) {
            return true;
         }
         // Caret at the very end of a text node: a following chip would be removed by Delete.
         if (offset === (container.textContent?.length ?? 0) && isChip(container.nextSibling)) {
            return true;
         }
      }

      return false;
   }
}
